/**
 * Section 06a — Watchlist hub.
 *
 * A live board for a chosen watchlist: per symbol the price, 24h %change,
 * a sparkline, the OverView score/zone (chart TF) + the aligned HTF zone,
 * and quick actions (chart, price alert, overview drawer). Scores are the
 * same as the Charts page (computed via computeTimeframeTrends server-side).
 */
import React from 'react';
import { Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_watchlist: GuideSection = {
  id: 'watchlist',
  label: 'Watchlist',
  group: 'pages',
  icon: <VisibilityIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Watchlist</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        A single board to "size up" everything you're tracking. Pick a watchlist and the page shows, for every
        symbol in it, the latest price, 24h %change, a sparkline, and the same <strong>OverView</strong> trend
        score the Charts page shows — so you can scan your whole list for the strongest / weakest setups without
        opening each chart.
      </Typography>

      <H id="controls">Top controls</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Watchlist selector</strong> — choose any of your watchlists (the same lists you manage on the Strategies page). The count of symbols is shown next to each name.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Timeframe</strong> — 1h / 4h / 1D. Sets the candle interval the price, sparkline and OverView score are computed on. The aligned higher timeframe (HTF) follows your Strategies → Timeframe Mapping.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Add symbol</strong> — type to search and add a symbol to the selected watchlist; it appears on the board immediately.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Refresh</strong> — recomputes the board on demand. The board also auto-refreshes every minute; the last update time is shown.</>} />
        </ListItem>
      </List>

      <H id="columns">Columns</H>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Column</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Meaning</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><strong>Symbol</strong></TableCell><TableCell>Ticker + exchange. Click the name to open the <em>overview drawer</em>; click the chart icon to open it in Charts. Sortable.</TableCell></TableRow>
          <TableRow><TableCell><strong>Price</strong></TableCell><TableCell>Last close on the chosen timeframe (forming bar excluded).</TableCell></TableRow>
          <TableRow><TableCell><strong>24h %</strong></TableCell><TableCell>Day-over-day change from daily closes, shown as a green/red pill. Sortable.</TableCell></TableRow>
          <TableRow><TableCell><strong>Trend</strong></TableCell><TableCell>Sparkline of the recent closes, coloured green/red by net direction.</TableCell></TableRow>
          <TableRow><TableCell><strong>OverView</strong></TableCell><TableCell>The −100…+100 trend score for the chosen timeframe, tinted by zone (Strong Buy → Strong Short). Hover for the zone label. Sortable (default sort, strongest first).</TableCell></TableRow>
          <TableRow><TableCell><strong>HTF</strong></TableCell><TableCell>The zone of the aligned higher timeframe — a quick read on whether the bigger trend agrees.</TableCell></TableRow>
          <TableRow><TableCell><strong>Actions</strong></TableCell><TableCell>Open chart · Set price alert · Remove from watchlist.</TableCell></TableRow>
        </TableBody>
      </Table>

      <GuideCallout kind="info" title="Scores match the chart">
        OverView here is computed with the exact same engine (and your active Chart Settings weights/thresholds)
        the <OpenInApp to="/app-guide#charts" label="Charts" /> page uses — so a symbol scoring Strong Buy on the
        board reads the same when you open its chart. See <OpenInApp to="/app-guide#charts" label="OverView trend meter" />.
      </GuideCallout>

      <H id="overview-drawer">Overview drawer</H>
      <P>
        Click a symbol name (here, on a strategy/symbol cell elsewhere, or from the Command Palette) to slide out
        the <strong>overview drawer</strong> — a compact "size up this symbol" panel with its latest Edge AI
        snapshot (OverView scores + recent forward-move history when available) and quick actions to open the chart
        or set a price alert. It's the one place to glance at a symbol without page-hopping.
      </P>

      <GuideCallout kind="tip">
        The board is bounded for performance — it computes the first ~50 symbols of a watchlist in small batches.
        Keep working lists focused, or split a very large universe into a few themed watchlists.
      </GuideCallout>
    </>
  ),
  searchIndex: [
    { heading: 'Watchlist hub',     anchor: 'controls',        body: 'watchlist hub board selector timeframe 1h 4h 1d add symbol refresh auto refresh' },
    { heading: 'Watchlist columns', anchor: 'columns',         body: 'watchlist columns price 24h change sparkline overview score zone htf actions chart price alert remove sortable' },
    { heading: 'Overview drawer',   anchor: 'overview-drawer', body: 'overview drawer size up symbol edge snapshot price alert chart' },
  ],
};

export default Section_watchlist;
